import React, { useState, useEffect } from "react";
import { Card, ListGroup, Dropdown, Spinner } from "react-bootstrap";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "../modals/ResourceViewerModal";
import { formatLocalDate } from "../utils/dateUtils";
import { useStore } from "react-redux";
import SpecimenIcon from "../assets/specimen-1.png"; // <-- replace with your icon
import { resolveName } from "../utils/messageUtils";

function SpecimenCard({
    title,
    inContext,
    specimenData,
    fhirClient,
    patientId,
    enableSearch = false,
    onSpecimenChange,
    isReply,
    depth = 6,
}) {
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [modalSpecimenDetails, setModalSpecimenDetails] = useState(null);
    const [specimenHistory, setSpecimenHistory] = useState([]);
    const [selectedSpecimen, setSelectedSpecimen] = useState(specimenData || null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const handleModalClose = () => setShowModal(false);
     const store = useStore();

    if (!title)
        title = resolveName(specimenData, fhirClient, store);

    /** Sync incoming specimen */
    useEffect(() => {
        if (specimenData?.error) {
            setError(`Error loading specimen (code: ${specimenData.error}).`);
            setSelectedSpecimen(null);
        } else {
            setError("");
            setSelectedSpecimen(specimenData || null);
        }
    }, [specimenData]);

    /** Fetch patient specimen history */
    useEffect(() => {
        if (!enableSearch || !patientId || !fhirClient) return;

        const fetchSpecimens = async () => {
            setHistoryLoading(true);
            try {
                const response = await fhirClient.request(
                    `Specimen?patient=Patient/${patientId}&_sort=-receivedTime&_count=10`
                );
                const found = response.entry?.map((e) => e.resource) || [];
                setSpecimenHistory(found);
            } catch (e) {
                console.warn("Specimen history fetch error:", e);
                setSpecimenHistory([]);
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchSpecimens();
    }, [enableSearch, patientId, fhirClient]);

    /** Auto-select newest specimen */
    useEffect(() => {
        if (!isReply && enableSearch) {
            if (specimenHistory.length > 0) {
                const first = specimenHistory[0];
                setSelectedSpecimen(first);
                if (onSpecimenChange) onSpecimenChange(first);
            } else {
                setSelectedSpecimen(null);
                if (onSpecimenChange) onSpecimenChange(null);
            }
        }
    }, [specimenHistory]);

    /** Open modal with fully loaded resource */
    const handleModalOpen = async () => {
        if (!selectedSpecimen?.id) return;

        try {
            const details = await fhirClient.request(`Specimen/${selectedSpecimen.id}`);
            setModalSpecimenDetails(details);
        } catch {
            setModalSpecimenDetails(null);
        }
        setShowModal(true);
    };

    const handleSelectSpecimen = (s) => {
        setSelectedSpecimen(s);
        if (onSpecimenChange) onSpecimenChange(s);
    };

    /** Field rendering */
    const fieldRenderers = [
        <ListGroup.Item className={styles.listItem} key="type">
            <strong>Type:</strong> {selectedSpecimen?.type?.text || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="status">
            <strong>Status:</strong> {selectedSpecimen?.status || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="receivedTime">
            <strong>Received:</strong>{" "}
            {formatLocalDate(selectedSpecimen?.receivedTime)}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="collectionTime">
            <strong>Collection:</strong>{" "}
            {formatLocalDate(selectedSpecimen?.collection?.collectedDateTime)}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="collector">
            <strong>Collector:</strong>{" "}
            {selectedSpecimen?.collection?.collector?.display ||
                selectedSpecimen?.collection?.collector?.reference ||
                "N/A"}
        </ListGroup.Item>,
    ];

    return (
        <>
            <Card className={styles.shrinkCard}>
                {/* HEADER */}
                <Card.Header
                    className={`d-flex justify-content-between align-items-center ${
                        inContext ? styles.inContextCardHeader : styles.cardHeader
                    }`}
                    onClick={handleModalOpen}
                    style={{ cursor: "pointer" }}
                >
                    {/* LEFT SIDE */}
                    <div className="d-flex align-items-center" style={{ pointerEvents: "none" }}>
                        <img src={SpecimenIcon} alt="Specimen Icon" className={styles.fhirIcon} />

                        <h6 className="mb-0" style={{ marginLeft: "0.5rem" }}>
                            {title?.length > 75
                                ? `Specimen: ${title.substring(0, 75)}…`
                                : `Specimen: ${title}`}
                        </h6>
                    </div>

                    {/* RIGHT SIDE */}
                    <div onClick={(e) => e.stopPropagation()}>
                        {depth > 0 && enableSearch && !isReply && (
                            historyLoading ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                            ) : (
                                <Dropdown
                                    onSelect={(eventKey) =>
                                        handleSelectSpecimen(
                                            specimenHistory.find((s) => s.id === eventKey)
                                        )
                                    }
                                >
                                    <Dropdown.Toggle variant="outline-primary" size="sm">
                                        Select Specimen
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto" }}>
                                        {specimenHistory.map((s) => (
                                            <Dropdown.Item key={s.id} eventKey={s.id}>
                                                {s.type?.text || "Specimen"} ·{" "}
                                                {formatLocalDate(s.receivedTime)}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )
                        )}
                    </div>
                </Card.Header>

                {/* BODY */}
                {depth > 0 && (
                    <Card.Body>
                        {error ? (
                            <p className={styles.errorText}>{error}</p>
                        ) : (
                            <ListGroup variant="flush">
                                {fieldRenderers.slice(0, depth)}
                            </ListGroup>
                        )}
                    </Card.Body>
                )}
            </Card>

            {/* MODAL VIEWER */}
            <GenericFHIRViewerModal
                show={showModal}
                onClose={handleModalClose}
                resource={modalSpecimenDetails}
                //title={selectedSpecimen?.type?.text || "Specimen Resource"}
                fhirClient={fhirClient}
                //icon={SpecimenIcon}
            />
        </>
    );
}

export default SpecimenCard;
